// custom-ui/HomeActionStack.tsx
// The "attend to this next" engine for the guided Home surfaces. Each role
// (employer / candidate) declares an ORDERED array of HomeAction rules; this
// component renders the ones whose `when` is true, top-to-bottom, with the
// first match as the hero. Reorder/add/remove a rule = edit one array entry.
//
// Design goals (kept deliberately simple so it stays easy to extend):
//   - priority = array order (first active = hero, sage accent + solid CTA)
//   - nothing is ever dropped: beyond `initialVisible` we collapse behind a
//     "Visa fler (N)" toggle rather than silently capping
//   - `fallback` renders when nothing matches (e.g. "Allt är i ordning")
//   - cards enter with a gentle staggered fade/slide (no jumpy pop-in)
import React, { useState } from 'react';
import { LucideIcon, ChevronDown, ChevronUp } from 'lucide-react';
import { HomeActionCard } from './HomeActionCard';

export type HomeActionTone = 'brand' | 'neutral' | 'green' | 'amber' | 'red' | 'blue';

export interface HomeAction {
    key: string;
    // Whether this rule applies right now (computed by the Home from its data).
    when: boolean;
    // Semantic concern (e.g. 'invitation', 'application'). When a data card and a
    // notification card share a dedupeKey, the notification is dropped so each
    // concern shows once (the richer data card wins).
    dedupeKey?: string;
    icon?: LucideIcon;
    // Custom leading visual (e.g. <LinkAvatar/>) — overrides `icon`.
    iconNode?: React.ReactNode;
    tone?: HomeActionTone;
    title: string;
    description?: string;
    ctaLabel: string;
    ctaIcon?: LucideIcon;
    onClick: () => void;
}

interface HomeActionStackProps {
    actions: HomeAction[];
    // Shown only when no rule matches (the "you're all caught up" state).
    fallback?: HomeAction;
    // How many cards show before the rest collapse behind "Visa fler". The
    // remainder is never dropped — just one click away. Default 5.
    initialVisible?: number;
}

export const HomeActionStack: React.FC<HomeActionStackProps> = ({ actions, fallback, initialVisible = 5 }) => {
    const [expanded, setExpanded] = useState(false);
    const matched = actions.filter((a) => a.when);
    const list = matched.length > 0 ? matched : fallback ? [fallback] : [];
    const visible = expanded ? list : list.slice(0, initialVisible);
    const hidden = list.length - visible.length;

    return (
        <div className="space-y-3">
            {visible.map((a, i) => (
                <div
                    key={a.key}
                    className="animate-in fade-in slide-in-from-bottom-2 duration-300"
                    style={{ animationDelay: `${i * 60}ms`, animationFillMode: 'backwards' }}
                >
                    <HomeActionCard
                        primary={i === 0}
                        icon={a.icon}
                        iconNode={a.iconNode}
                        tone={a.tone}
                        title={a.title}
                        description={a.description}
                        ctaLabel={a.ctaLabel}
                        ctaIcon={a.ctaIcon}
                        onClick={a.onClick}
                    />
                </div>
            ))}

            {(hidden > 0 || expanded) && list.length > initialVisible && (
                <button
                    onClick={() => setExpanded((v) => !v)}
                    className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-50 transition-colors"
                >
                    {expanded
                        ? (<><ChevronUp className="w-4 h-4" /> Visa färre</>)
                        : (<><ChevronDown className="w-4 h-4" /> Visa fler ({hidden})</>)}
                </button>
            )}
        </div>
    );
};

export default HomeActionStack;
